import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Card, Button, Skeleton, Field, Input, Toast } from "@/ui";

export default function Loyalty() {
  const { shopId, can } = useAuth();
  const navigate = useNavigate();
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const canEdit = !!can?.add_product_change_price;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/loyalty${shopId ? `?shopId=${shopId}` : ""}`);
      setRules(r.data.rules);
    } catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, [shopId]);
  useEffect(() => { if (!shopId) { navigate("/pick-shop", { replace: true }); return; } load(); }, [shopId, navigate, load]);

  const set = (k, v) => setRules((r) => ({ ...r, [k]: v }));

  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.put("/loyalty", {
        shopId,
        pesosPerPoint: parseInt(rules.pesosPerPoint, 10) || 1,
        rewardThreshold: parseInt(rules.rewardThreshold, 10) || 0,
        rewardValueCentavos: Math.round((parseFloat(rules.rewardValuePesos ?? (rules.rewardValueCentavos / 100)) || 0) * 100),
        rewardText: rules.rewardText,
      });
      setToast("points settings saved");
      setTimeout(() => setToast(""), 2500);
    } catch (e) { setError(friendlyError(e)); }
    finally { setSaving(false); }
  };

  const perPeso = rules ? (1 / (parseInt(rules.pesosPerPoint, 10) || 1)) : 0;

  return (
    <Layout>
      <div style={{ marginBottom: 8 }}>
        <h1 className="t-h1">loyalty</h1>
        <p className="muted">customers earn points on every sale — chat, shop page and counter. you decide how fast.</p>
      </div>

      {loading ? (
        <Card><Skeleton style={{ height: 180 }} /></Card>
      ) : error && !rules ? (
        <Card data-testid="loyalty-error" className="text-sm text-rose-600">{error}</Card>
      ) : rules ? (
        <div className="stack" style={{ gap: 16 }}>
          <Card data-testid="loyalty-rules-card" className="stack" style={{ gap: 16 }}>
            <div className="row" style={{ gap: 8 }}>
              <div className="rounded-full p-2" style={{ background: "var(--sunk)" }}><Gift size={18} className="text-violet-600" /></div>
              <div>
                <div className="text-lg font-semibold">How points are earned</div>
                <div className="text-sm text-slate-500">Earning only for now — spending points comes later.</div>
              </div>
            </div>

            <Field label="Pesos per point" hint={`right now that's 1 point for every \u20b1${rules.pesosPerPoint} a customer spends`} htmlFor="ppp">
              <Input id="ppp" data-testid="pesos-per-point" inputMode="numeric" disabled={!canEdit}
                value={rules.pesosPerPoint ?? ""} onChange={(e) => set("pesosPerPoint", e.target.value.replace(/[^0-9]/g, ""))} style={{ width: 120 }} />
            </Field>

            <div className="rounded-xl px-4 py-3 text-sm" style={{ background: "#f8fafc" }} data-testid="loyalty-preview">
              A customer who spends <strong>{"\u20b1"}500</strong> earns <strong>{Math.floor(500 * perPeso)}</strong> point(s).
            </div>

            <Field label="Reward text (shown to customers)" htmlFor="rt">
              <Input id="rt" data-testid="reward-text" disabled={!canEdit}
                value={rules.rewardText ?? ""} onChange={(e) => set("rewardText", e.target.value)} />
            </Field>
            <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>
              <Field label="Points needed for a reward" htmlFor="rth">
                <Input id="rth" data-testid="reward-threshold" inputMode="numeric" disabled={!canEdit}
                  value={rules.rewardThreshold ?? ""} onChange={(e) => set("rewardThreshold", e.target.value.replace(/[^0-9]/g, ""))} style={{ width: 120 }} />
              </Field>
            </div>

            {canEdit ? (
              <div><Button variant="primary" data-testid="save-loyalty" loading={saving} onClick={save}>save</Button></div>
            ) : null}
            {error ? <div className="err-text" data-testid="loyalty-save-error">{error}</div> : null}
          </Card>
        </div>
      ) : null}
      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="loyalty-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
